import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useWidth } from '../../lib/useWidth';
import { Spinner } from '../../components/shared';
import TaxonomyGroupFilter from '../../components/farm-util/TaxonomyGroupFilter';
import CatchSpatModal from '../../components/farm-modals/CatchSpatModal';
import ExtraSeedingsTable from '../../components/lines/ExtraSeedingsTable';
import HarvestsTable from '../../components/lines/HarvestsTable';
import PendingDataTable from '../../components/lines/PendingDataTable';
import GrowerHarvestModal from '../../components/farm-modals/GrowerHarvestModal';
import GrowerSeedModal from '../../components/farm-modals/GrowerSeedModal';
import SeedingModal from '../../components/farm-modals/SeedingModal';
import MaintenanceModal from '../../components/farm-modals/MaintenanceModal';
import FloatManageModal from '../../components/farm-modals/FloatManageModal';
import AssessmentModal from '../../components/farm-modals/AssessmentModal';
import HarvestCompleteModal from '../../components/farm-modals/HarvestCompleteModal';
import TimelineView from '../../components/lines/TimelineView';
import { sendSingleRequest } from '../../apis';
import {
  IFarmWeatherResource,
  ILineResource,
  IMusselLineDetail,
  IOysterLineDetail,
} from '../../entities/farms.entities';
import { ICycleResource } from '../../entities/growing.entities';
import { showFeedback } from '../../store/ui/ui.actions';
import LineFormModal from '../../components/farm-modals/LineFormModal';
import LineTemplateDesktop from './LineTemplateDesktop';
import LineTemplateMobile from './LineTemplateMobile';
import MaintenanceTable from '../../components/lines/MaintenanceTable';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  SelectFarmsByFarmID,
  SelectIsGrower,
} from '../../store/extra/extra.selector';
import LineMetaModal from '../../components/lines/LineMetaModal';
import { loadFarmsData } from '../../store/farms/farms.actions';
import ReturnHarvestModal from '../../components/farm-modals/ReturnHarvestModal';
import WasteHarvestModal from '../../components/farm-modals/WasteHarvestModal';
import { toSecond } from '../../lib/common.helpers';

interface ISeasonDetailResponse {
  has_previous: boolean;
  has_next: boolean;
  season_data: ICycleResource | null;
}

const FarmLine = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const width = useWidth();
  const params = useParams<{ idFarm: string; idLine: string }>();
  const lang = useSelector(selectLang);
  const currentFarm = SelectFarmsByFarmID(+params.idFarm).find(
    x => x.id === +params.idFarm,
  );
  const isGrower = SelectIsGrower(+params.idFarm);

  const [lineData, setLineData] = useState<
    IMusselLineDetail | IOysterLineDetail
  >();
  const [loading, setLoading] = useState(false);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const isFirstLoadRef = useRef(true);
  const musselLine = lineData as IMusselLineDetail;

  const [seasonData, setSeasonData] = useState<ICycleResource | null>(null);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [isVirtualEmpty, setIsVirtualEmpty] = useState(false);
  const currentSeasonDateRef = useRef<number | null>(null);
  const [nextDateStack, setNextDateStack] = useState<(number | null)[]>([]);

  const curCycle = seasonData;
  const mainSeedResource = curCycle?.seedings?.find(x => !x.parent_id);
  const musselCycle = seasonData ?? undefined;

  const handleClickPrevGroup = () => {
    if (isVirtualEmpty) {
      setIsVirtualEmpty(false);
      loadSeasonData(currentSeasonDateRef.current);
      return;
    }
    if (!hasPrevious || !seasonData) return;
    const prevDate = seasonData.main_seed.planned_date_seed - 1;
    setNextDateStack(prev => [...prev, currentSeasonDateRef.current]);
    currentSeasonDateRef.current = prevDate;
    loadSeasonData(prevDate);
  };
  const handleClickNextGroup = () => {
    if (isVirtualEmpty) return;
    if (hasNext && seasonData) {
      if (nextDateStack.length > 0) {
        const stack = [...nextDateStack];
        const nextDate = stack.pop()!;
        setNextDateStack(stack);
        currentSeasonDateRef.current = nextDate;
        loadSeasonData(nextDate);
        return;
      }
      const nextDate = seasonData.main_seed.planned_date_seed + 1;
      currentSeasonDateRef.current = nextDate;
      loadSeasonData(nextDate);
      return;
    }
    if (!hasNext && seasonData && !seasonData.main_seed.is_growing) {
      setSeasonData(null);
      setHasPrevious(true);
      setHasNext(false);
      setIsVirtualEmpty(true);
    }
  };
  const canGoNext = isVirtualEmpty
    ? false
    : hasNext || (seasonData !== null && !seasonData.main_seed.is_growing);

  const [editLineData, setEditLineData] = useState<ILineResource>();
  const [seedModal, setSeedModal] = useState(false);
  const [visibleAssess, setVisibleAssess] = useState(false);
  const [editSeedModal, setEditSeedModal] = useState(false);
  const [maintenanceModal, setMaintenanceModal] = useState(false);
  const [visibleInventories, setVisibleInventories] = useState(false);
  const [visibleExtraSeed, setVisibleExtraSeed] = useState(false);
  const [visibleCatchSpat, setVisibleCatchSpat] = useState(false);
  const [visibleHarvestModal, setVisibleHarvestModal] = useState(false);
  const [visibleReturnModal, setVisibleReturnModal] = useState(false);
  const [visibleTimeline, setVisibleTimeline] = useState(0);
  const [timelines, setTimelines] = useState<any[]>();
  const [weathers, setWeathers] = useState<IFarmWeatherResource[]>();
  const [visibleMeta, setVisibleMeta] = useState(false);
  const [visibleWaste, setVisibleWaste] = useState(false);
  const [fltTaxonomyIds, setFltTaxonomyIds] = useState<number[]>([]);

  const toggleVisibleTimeline = async () => {
    if (!curCycle && (!lineData || lineData.seedings.length <= 0)) return;
    if (visibleTimeline) {
      setTimelines(undefined);
      setVisibleTimeline(0);
    } else {
      setVisibleTimeline(-1);
      try {
        const cycleID = curCycle
          ? curCycle.main_seed.id
          : lineData!.seedings[lineData!.seedings.length - 1].id;
        const resp = await sendSingleRequest(
          { id: cycleID, with_history: true },
          'GET',
          'api/farm/line/seeding',
          true,
        );
        if (resp.status) {
          const {
            seedings = [],
            assessments = [],
            maintenances = [],
            inventory_seeds = [],
            harvests = [],
            seed_manages = [],
          } = resp.data ?? {};
          let min_date = -1;
          for (let x of seedings) {
            if (min_date === -1 || min_date > x.planned_date_seed)
              min_date = x.planned_date_seed;
          }
          for (let x of assessments) {
            if (min_date === -1 || min_date > x.assessment_date)
              min_date = x.assessment_date;
          }
          for (let x of maintenances) {
            if (min_date === -1 || min_date > x.maintain_date)
              min_date = x.maintain_date;
          }
          for (let x of inventory_seeds) {
            if (min_date === -1 || min_date > x.manage_date)
              min_date = x.manage_date;
          }
          for (let x of harvests) {
            if (min_date === -1 || min_date > x.complete_date)
              min_date = x.complete_date;
          }
          for (let x of seed_manages) {
            if (min_date === -1 || min_date > x.manage_date)
              min_date = x.manage_date;
          }
          min_date = min_date < 0 ? Date.now() : min_date * 1000;
          const wsp = await sendSingleRequest(
            { min_date, farm_id: params.idFarm },
            'GET',
            'api/farm/weather',
            true,
          );
          const lsp = await sendSingleRequest(
            {},
            'GET',
            `api/farm/line/tag-logs/${params.idLine}`,
            true,
          );
          let logs: any[] = [];
          if (lsp.status) {
            for (let x of lsp.data) {
              if (!x.is_on) {
                logs.push(
                  {
                    id: x.id,
                    tag: x.tag,
                    is_on: true,
                    logged_date: x.created_at,
                    user: x.user,
                  },
                  {
                    id: x.id,
                    tag: x.tag,
                    is_on: false,
                    logged_date: x.updated_at,
                    user: x.user,
                  },
                );
              } else {
                logs.push({
                  id: x.id,
                  tag: x.tag,
                  is_on: true,
                  logged_date: x.created_at,
                  user: x.user,
                });
              }
            }
          }
          const bsp = await sendSingleRequest(
            {
              line_id: params.idLine,
              rel_type: 'WATER_QUALITY',
              date_from: toSecond(min_date),
            },
            'GET',
            'api/report/biosecurity',
            true,
          );
          let biosecurityData: any[] = [];
          if (bsp.status) {
            biosecurityData = bsp.data;
          }
          const fsp = await sendSingleRequest(
            {},
            'GET',
            `api/farm/farm-status/${params.idFarm}`,
            true,
          );
          const farmStatusData: any[] = fsp.status ? (fsp.data ?? []) : [];
          let planned: any[] = [];
          for (let seed of seedings) {
            const dates = seed.planned_date_history ?? [];
            for (let i = 0; i < dates.length - 1; i++) {
              if (dates[i] !== dates[i + 1]) {
                planned.push({
                  id: `${seed.id} - ${i}`,
                  old_date: dates[i],
                  new_date: dates[i + 1],
                });
              }
            }
          }
          setTimelines([
            ...seedings.map((x: any) => ({ type: 'SEEDING', data: x })),
            ...assessments.map((x: any) => ({
              type: 'ASSESSMENT',
              data: x,
            })),
            ...maintenances.map((x: any) => ({
              type: 'MAINTENANCE',
              data: x,
            })),
            ...inventory_seeds.map((x: any) => ({
              type: 'INVENTORY_SEED',
              data: x,
            })),
            ...harvests.map((x: any) => ({ type: 'HARVEST', data: x })),
            ...seed_manages.map((x: any) => ({ type: 'SEED_MANAGE', data: x })),
            ...logs.map((x: any) => ({ type: 'TAG_LOG', data: x })),
            ...planned.map((x: any) => ({ type: 'HARVEST_PLANNED', data: x })),
            ...biosecurityData.map((x: any) => ({
              type: 'WATER_QUALITY',
              data: x,
            })),
            ...farmStatusData.map((x: any) => ({
              type: 'FARM_STATUS',
              data: x,
            })),
          ]);
          if (wsp.status) {
            setWeathers(wsp.data);
          }
          setVisibleTimeline(1);
        } else {
          setVisibleTimeline(0);
          alert(resp.data?.message);
          return;
        }
      } catch (e) {
        console.error('toggleVisibleTimeline error:', e);
        setVisibleTimeline(0);
      }
    }
  };

  const loadSeasonData = useCallback(
    async (seasonDate: number | null = null, defaultToEmpty = false) => {
      setSeasonLoading(true);
      const reqParams: Record<string, any> = { line_id: params.idLine };
      if (seasonDate !== null) reqParams.season_date = seasonDate;
      const resp = await sendSingleRequest(
        reqParams,
        'GET',
        'api/farm/line/season-detail',
        true,
      );
      if (resp.status) {
        const { has_previous, has_next, season_data } =
          resp.data as ISeasonDetailResponse;
        if (defaultToEmpty && season_data) {
          setSeasonData(null);
          setHasPrevious(true);
          setHasNext(false);
          setIsVirtualEmpty(true);
        } else {
          setSeasonData(season_data);
          setHasPrevious(has_previous);
          setHasNext(has_next);
          if (season_data) setIsVirtualEmpty(false);
        }
      }
      setSeasonLoading(false);
    },
    [params.idLine],
  );

  const loadData = useCallback(
    async (isInternal = false) => {
      if (!isInternal) {
        setLoading(true);
      }
      const response = await sendSingleRequest(
        { with_harvest_spats: true },
        'GET',
        `api/farm/line/lines/${params.idLine}`,
        true,
      );
      if (response.status) {
        const data = response.data;
        setLineData(data);
        const isFirst = isFirstLoadRef.current;
        isFirstLoadRef.current = false;
        const defaultToEmpty = isFirst && !data.growing_cycle;
        await loadSeasonData(currentSeasonDateRef.current, defaultToEmpty);
      } else {
        dispatch(
          showFeedback({
            isMessage: true,
            type: 'error',
            message: translate(lang, '_failed_load_info'),
            duration: -1,
          }),
        );
        history.replace(`/farms/${params.idFarm}`);
      }
      if (!isInternal) {
        setLoading(false);
      }
    },
    [params.idLine, params.idFarm, loadSeasonData],
  );
  const deleteLineClick = async () => {
    if (!window.confirm('Are you sure you want to delete this line?')) return;
    const response = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/line/lines/${lineData?.id}`,
      true,
    );
    if (response.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: 'Line deleted successfully',
        }),
      );
      history.replace(`/farms/${params.idFarm}`);
      dispatch(loadFarmsData());
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: response.data?.message ?? 'Unknown error',
          duration: -1,
        }),
      );
    }
  };

  useEffect(() => {
    currentSeasonDateRef.current = null;
    isFirstLoadRef.current = true;
    setNextDateStack([]);
    setSeasonData(null);
    setHasPrevious(false);
    setHasNext(false);
    setIsVirtualEmpty(false);
  }, [params.idLine]);

  useEffect(() => {
    setTimelines(undefined);
    setVisibleTimeline(0);
  }, [curCycle]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredCycle = useMemo(() => {
    if (!curCycle) return null;
    if (fltTaxonomyIds.length === 0) return curCycle;

    const seedings = curCycle.seedings.filter(s =>
      s.taxonomy_groups?.some(t => fltTaxonomyIds.includes(t.id)),
    );
    const assessments = curCycle.assessments.filter(s => {
      if (!s.batch_seed_id) return true;
      const seed = curCycle.seedings.find(sd => sd.id === s.batch_seed_id);
      return (
        seed && seed.taxonomy_groups?.some(t => fltTaxonomyIds.includes(t.id))
      );
    });
    const harvests = curCycle.harvests.filter(h =>
      h.taxonomy_groups?.some(t => fltTaxonomyIds.includes(t.id)),
    );

    return { ...curCycle, seedings, assessments, harvests };
  }, [curCycle, fltTaxonomyIds]);

  if (!lineData || !currentFarm) {
    return (
      <div className='h-calc-80 bg-secondary'>
        <div className='container pos-relative'>
          <Spinner position='global' />
        </div>
      </div>
    );
  }
  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative'>
        <div className='farms__harvest pb-36'>
          {width <= 768 ? (
            <LineTemplateMobile
              lineData={lineData}
              cyclePart={curCycle}
              isNextEnabled={canGoNext}
              isPrevEnabled={hasPrevious}
              onClickPrevGroup={handleClickPrevGroup}
              onClickNextGroup={handleClickNextGroup}
              showEditLineModal={() => setEditLineData(lineData)}
              onSeedingClick={() => setSeedModal(true)}
              onAssessmentClick={() => setVisibleAssess(true)}
              onCatchSpatClick={() => setVisibleCatchSpat(true)}
              onHarvestClick={() => setVisibleHarvestModal(true)}
              onReturnClick={() => setVisibleReturnModal(true)}
              onMaintenanceClick={() => setMaintenanceModal(true)}
              onFloatingManageClick={() => setVisibleInventories(true)}
              onExtraSeedClick={() => setVisibleExtraSeed(true)}
              onMetaEditClick={() => setVisibleMeta(true)}
              checkedHistoryView={visibleTimeline}
              onHistoryViewClick={toggleVisibleTimeline}
              onWasteClick={() => setVisibleWaste(true)}
            />
          ) : (
            <LineTemplateDesktop
              loadLine={loadData}
              lineData={lineData}
              cyclePart={curCycle}
              isNextEnabled={canGoNext}
              isPrevEnabled={hasPrevious}
              onClickPrevGroup={handleClickPrevGroup}
              onClickNextGroup={handleClickNextGroup}
              showEditLineModal={() => setEditLineData(lineData)}
              onDeleteClick={deleteLineClick}
              onSeedingClick={() => setSeedModal(true)}
              onAssessmentClick={() => setVisibleAssess(true)}
              onCatchSpatClick={() => setVisibleCatchSpat(true)}
              onHarvestClick={() => setVisibleHarvestModal(true)}
              onReturnClick={() => setVisibleReturnModal(true)}
              onMaintenanceClick={() => setMaintenanceModal(true)}
              onFloatingManageClick={() => setVisibleInventories(true)}
              onExtraSeedClick={() => setVisibleExtraSeed(true)}
              onMetaEditClick={() => setVisibleMeta(true)}
              checkedHistoryView={visibleTimeline}
              onHistoryViewClick={toggleVisibleTimeline}
              onWasteClick={() => setVisibleWaste(true)}
            />
          )}
          {visibleTimeline === 1 &&
            (timelines ? (
              <TimelineView
                dataItems={timelines}
                isGrowing={curCycle?.main_seed.is_growing}
                weathers={weathers}
                lineData={lineData}
              />
            ) : (
              <div className='mt-17 mb-17'>
                <Spinner />
              </div>
            ))}
          {!isGrower && (
            <PendingDataTable lineData={musselLine} loadLine={loadData} />
          )}
          {curCycle && (
            <TaxonomyGroupFilter
              className='mb-10'
              value={fltTaxonomyIds}
              onChange={setFltTaxonomyIds}
            />
          )}
          <div className='w-100'>
            {filteredCycle ? (
              <MaintenanceTable
                type={filteredCycle.crop_type}
                assessments={filteredCycle.assessments}
                maintenances={filteredCycle.maintenances}
                floats={filteredCycle.inventory_seeds}
                seed_manages={filteredCycle.seed_manages}
                lineData={lineData}
                loadLine={loadData}
              />
            ) : (
              lineData.maintenances && (
                <MaintenanceTable
                  type={lineData.crop_type}
                  maintenances={lineData.maintenances}
                  lineData={lineData}
                  loadLine={loadData}
                />
              )
            )}
          </div>
          <div className='w-100 mt-10'>
            {filteredCycle && (
              <ExtraSeedingsTable
                loadLine={loadData}
                lineData={lineData}
                cyclePart={filteredCycle}
              />
            )}
          </div>
          {filteredCycle && (
            <div className='w-100 mt-10'>
              <HarvestsTable
                cropType={filteredCycle.crop_type}
                lineData={lineData}
                harvests={filteredCycle.harvests}
                seedings={filteredCycle.seedings}
                onUpdateRow={loadData}
                farmID={currentFarm.id}
              />
            </div>
          )}
        </div>
        {(loading || seasonLoading) && <Spinner position='global' />}
      </div>
      {seedModal && (
        <>
          {isGrower ? (
            <GrowerSeedModal
              visible={seedModal}
              onClose={() => setSeedModal(false)}
              lineData={lineData}
              onConfirm={() => {
                setSeedModal(false);
                loadData();
              }}
            />
          ) : (
            <SeedingModal
              visible={seedModal}
              title='Seed the line'
              lineData={lineData}
              onCancel={() => setSeedModal(false)}
              onConfirm={() => {
                setSeedModal(false);
                loadData();
              }}
            />
          )}
        </>
      )}
      {visibleAssess && curCycle && (
        <AssessmentModal
          title='Add assessment'
          visible={visibleAssess}
          onCancel={() => setVisibleAssess(false)}
          lineData={lineData}
          onConfirm={() => {
            setVisibleAssess(false);
            loadData();
          }}
        />
      )}
      {!!editLineData && (
        <LineFormModal
          visible={true}
          lineData={editLineData}
          onClose={u => {
            setEditLineData(undefined);
            if (u === true) loadData();
          }}
          farmData={currentFarm}
        />
      )}
      {editSeedModal && curCycle && (
        <>
          {isGrower ? (
            <GrowerSeedModal
              lineData={lineData}
              seedData={mainSeedResource}
              visible={editSeedModal}
              onClose={() => setEditSeedModal(false)}
              onConfirm={() => {
                setEditSeedModal(false);
                loadData();
              }}
            />
          ) : (
            <SeedingModal
              visible={editSeedModal}
              title='Update seeding data'
              lineData={lineData}
              onCancel={() => setEditSeedModal(false)}
              data={mainSeedResource}
              updateID={curCycle.main_seed.id}
              onConfirm={() => {
                setEditSeedModal(false);
                loadData();
              }}
            />
          )}
        </>
      )}
      {visibleHarvestModal && (
        <>
          {isGrower ? (
            <GrowerHarvestModal
              type={lineData.crop_type}
              visible={true}
              lineData={lineData}
              onClose={() => setVisibleHarvestModal(false)}
              onConfirm={() => {
                setVisibleHarvestModal(false);
                loadData();
              }}
            />
          ) : (
            <HarvestCompleteModal
              visible={true}
              title='Harvest Complete'
              onCancel={() => setVisibleHarvestModal(false)}
              onConfirm={d => {
                setVisibleHarvestModal(false);
                if (d?.data?.is_final === false || d?.data?.is_final === 0) {
                  history.push(`/spat-storage?type=${lineData.crop_type}`);
                } else {
                  loadData();
                }
              }}
              lineData={lineData}
            />
          )}
        </>
      )}
      {visibleCatchSpat && (
        <CatchSpatModal
          title='Catch Spat'
          visible={true}
          onCancel={() => setVisibleCatchSpat(false)}
          lineData={lineData}
          onConfirm={() => {
            setVisibleCatchSpat(false);
            loadData();
          }}
          type={lineData.crop_type}
        />
      )}
      {maintenanceModal && (
        <MaintenanceModal
          visible={true}
          title='Add maintenance'
          onCancel={() => setMaintenanceModal(false)}
          onConfirm={() => {
            setMaintenanceModal(false);
            loadData();
          }}
          lineData={lineData}
        />
      )}
      {visibleInventories && (
        <FloatManageModal
          visible={visibleInventories}
          onCancel={() => setVisibleInventories(false)}
          title={`Inventory Management`}
          onConfirm={() => {
            setVisibleInventories(false);
            loadData();
          }}
          lineData={lineData}
          manageCycle={
            lineData.crop_type === 'MUSSEL' ? musselCycle : undefined
          }
        />
      )}
      {visibleExtraSeed && lineData.growing_cycle && (
        <>
          {isGrower ? (
            <GrowerSeedModal
              lineData={lineData}
              visible={visibleExtraSeed}
              onClose={() => setVisibleExtraSeed(false)}
              onConfirm={() => {
                setVisibleExtraSeed(false);
                loadData();
              }}
            />
          ) : (
            <SeedingModal
              visible={visibleExtraSeed}
              title='Extra seed the line'
              onCancel={() => setVisibleExtraSeed(false)}
              onConfirm={() => {
                setVisibleExtraSeed(false);
                loadData();
              }}
              lineData={lineData}
              parentID={lineData.growing_cycle.main_seed.id}
            />
          )}
        </>
      )}
      {visibleMeta && (
        <LineMetaModal
          visible={visibleMeta}
          onClose={() => setVisibleMeta(false)}
          lineData={lineData}
          loadData={loadData}
        />
      )}
      {visibleReturnModal && (
        <ReturnHarvestModal
          lineData={lineData}
          visible={true}
          onClose={() => setVisibleReturnModal(false)}
          onConfirm={() => {
            setVisibleReturnModal(false);
            loadData();
          }}
        />
      )}
      {visibleWaste && (
        <WasteHarvestModal
          lineData={lineData}
          visible={true}
          onCancel={() => setVisibleWaste(false)}
          onConfirm={() => {
            setVisibleWaste(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default FarmLine;
